import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Tabs, Tab, Tooltip, Drawer,
  Collapse, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ChatIcon from '@mui/icons-material/Chat';
import FolderIcon from '@mui/icons-material/Folder';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ShareIcon from '@mui/icons-material/Share';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import GroupIcon from '@mui/icons-material/Group';
import PeopleIcon from '@mui/icons-material/People';
import AgentManager from './AgentManager';
import PromptManager from './PromptManager';

function timeAgo(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'nu';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}u`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

const SIDEBAR_WIDTH = 340;

function SidebarContent({
  conversations,
  archivedConversations,
  showArchive,
  onToggleArchive,
  selectedId,
  onSelect,
  onNewChat,
  onNewChatInFolder,
  onDelete,
  onArchive,
  onUnarchive,
  agents,
  prompts,
  onRefreshAgents,
  onRefreshPrompts,
  userEmail,
  folders,
  selectedFolderId,
  onSelectFolder,
  onEditFolder,
  onDeleteFolder,
  onSearch,
  onPin,
  onRename,
  onDetachFromFolder,
  onStartGroupChat,
  onMoveToFolder,
  onOpenProjectBrowser,
  onUnpinProject,
  clients,
}) {
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [foldersOpen, setFoldersOpen] = useState(true);
  const [groupChatsOpen, setGroupChatsOpen] = useState(true);
  const [myChatsOpen, setMyChatsOpen] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuConv, setMenuConv] = useState(null);
  const [folderMenuAnchor, setFolderMenuAnchor] = useState(null);
  const [folderMenuFolder, setFolderMenuFolder] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState('');
  const [dragConvId, setDragConvId] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const debounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { onSearch(val); }, 300);
  };

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const openMenu = (e, conv) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuConv(conv);
  };
  const closeMenu = () => { setMenuAnchor(null); setMenuConv(null); };

  const handleMenuAction = (action, extra) => {
    const conv = menuConv;
    closeMenu();
    if (!conv) return;
    switch (action) {
      case 'pin': onPin(conv.id, !conv.is_pinned); break;
      case 'rename':
        setRenameTitle(conv.title);
        setRenameOpen(true);
        setMenuConv(conv); // keep ref for rename
        break;
      case 'archive': onArchive(conv.id); break;
      case 'unarchive': onUnarchive(conv.id); break;
      case 'delete': onDelete(conv.id); break;
      case 'detach': onDetachFromFolder(conv.id); break;
      case 'group': onStartGroupChat(conv.id); break;
      case 'moveToFolder': onMoveToFolder(conv.id, extra); break;
      default: break;
    }
  };

  const handleRenameSubmit = () => {
    if (menuConv && renameTitle.trim()) {
      onRename(menuConv.id, renameTitle.trim());
    }
    setRenameOpen(false);
    setMenuConv(null);
  };

  const displayedConversations = showArchive ? archivedConversations : conversations;

  // Split conversations into sections
  const groupConvs = displayedConversations.filter((c) => c.is_group && !c.folder_id);
  const myConvs = selectedFolderId
    ? displayedConversations.filter((c) => c.folder_id === selectedFolderId)
    : displayedConversations.filter((c) => !c.is_group || c.folder_id);

  const renderConvItem = (conv) => (
    <Box
      key={conv.id}
      onClick={() => onSelect(conv.id)}
      draggable
      onDragStart={(e) => {
        setDragConvId(conv.id);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(conv.id));
      }}
      onDragEnd={() => { setDragConvId(null); setDragOverFolderId(null); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.75,
        px: 1.5, py: 0.75, mx: 0.5, mb: 0.25,
        borderRadius: 1.5, cursor: 'grab',
        bgcolor: selectedId === conv.id ? '#d5e3fd' : 'transparent',
        '&:hover': { bgcolor: selectedId === conv.id ? '#d5e3fd' : '#f1f4f5' },
        opacity: dragConvId === conv.id ? 0.5 : 1,
        transition: 'background-color 0.15s, opacity 0.15s',
        '& .conv-menu-btn': { opacity: 0 },
        '&:hover .conv-menu-btn': { opacity: 1 },
      }}
    >
      {conv.is_pinned && <PushPinIcon sx={{ fontSize: 12, color: '#526075', flexShrink: 0, transform: 'rotate(45deg)' }} />}
      {conv.is_group ? (
        <Tooltip title={conv.participant_names ? `Gedeeld met: ${conv.participant_names}` : 'Groepschat'} arrow placement="right">
          <GroupIcon sx={{ fontSize: 16, color: '#8b5cf6', flexShrink: 0 }} />
        </Tooltip>
      ) : conv.participant_count > 0 ? (
        <Tooltip title={conv.participant_names ? `Gedeeld met: ${conv.participant_names}` : 'Gedeelde chat'} arrow placement="right">
          <PeopleIcon sx={{ fontSize: 16, color: '#8b5cf6', flexShrink: 0 }} />
        </Tooltip>
      ) : conv.agent_icon ? (
        <SmartToyIcon sx={{ fontSize: 16, color: '#526075', flexShrink: 0 }} />
      ) : (
        <ChatIcon sx={{ fontSize: 16, color: '#adb3b5', flexShrink: 0 }} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontSize={13} fontWeight={selectedId === conv.id ? 600 : 400} noWrap>
          {conv.title}
        </Typography>
        {(conv.agent_name || conv.client_name) && (
          <Typography fontSize={11} color="text.secondary" noWrap>
            {[conv.agent_name, conv.client_name].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>
      <Typography fontSize={10} color="text.secondary" sx={{ flexShrink: 0 }}>
        {timeAgo(conv.updated_at)}
      </Typography>
      <IconButton
        className="conv-menu-btn"
        size="small"
        onClick={(e) => openMenu(e, conv)}
        sx={{ p: 0.25, transition: 'opacity 0.15s' }}
      >
        <MoreHorizIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH, height: '100%',
        bgcolor: '#fff', display: 'flex', flexDirection: 'column',
      }}
    >
      {/* New Chat + Projects buttons */}
      <Box sx={{ p: 2, pb: 1, display: 'flex', gap: 1 }}>
        <Button
          variant="contained" startIcon={<AddIcon />} onClick={onNewChat}
          sx={{ flex: 1, bgcolor: '#526075', '&:hover': { bgcolor: '#465469' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          Nieuw gesprek
        </Button>
        <Tooltip title="Projecten browsen">
          <Button
            variant="outlined" onClick={onOpenProjectBrowser}
            sx={{ minWidth: 42, px: 1, borderColor: 'rgba(45,51,53,0.08)', color: '#526075', borderRadius: 2, '&:hover': { borderColor: '#526075', bgcolor: '#d5e3fd' } }}
          >
            <FolderIcon sx={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
        sx={{
          minHeight: 40,
          '& .MuiTab-root': { minHeight: 40, fontSize: 12, textTransform: 'none', py: 0 },
          '& .MuiTabs-indicator': { bgcolor: '#526075' },
        }}
      >
        <Tab icon={<ChatIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Chats" />
        <Tab icon={<SmartToyIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Agents" />
        <Tab icon={<DescriptionIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Prompts" />
      </Tabs>

      {/* Tab content */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {tab === 0 && (
          <Box>
            {/* Search */}
            <Box sx={{ px: 2, py: 1 }}>
              <TextField
                fullWidth size="small"
                placeholder={showArchive ? 'Zoek in archief...' : 'Zoek gesprekken...'}
                value={search} onChange={handleSearchChange}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }}
              />
            </Box>

            {!showArchive && (
              <>
                {/* Folders / Projects */}
                {folders && folders.length > 0 && (
                  <Box sx={{ mb: 0.5 }}>
                    <Box
                      onClick={() => setFoldersOpen(!foldersOpen)}
                      sx={{
                        display: 'flex', alignItems: 'center', px: 2, py: 0.5, cursor: 'pointer',
                        '&:hover': { bgcolor: '#f8f9fa' },
                      }}
                    >
                      <Typography fontSize={11} fontWeight={700} color="text.secondary"
                        sx={{ flex: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Gepinde Projecten
                      </Typography>
                      {foldersOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: '#adb3b5' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#adb3b5' }} />}
                    </Box>
                    <Collapse in={foldersOpen}>
                      {/* All chats */}
                      <Box
                        onClick={() => onSelectFolder(null)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.5, cursor: 'pointer',
                          bgcolor: selectedFolderId === null ? '#d5e3fd' : 'transparent',
                          '&:hover': { bgcolor: selectedFolderId === null ? '#d5e3fd' : '#f1f4f5' },
                        }}
                      >
                        <ChatIcon sx={{ fontSize: 14, color: '#adb3b5' }} />
                        <Typography fontSize={12} fontWeight={selectedFolderId === null ? 600 : 400}>Alle chats</Typography>
                      </Box>
                      {folders.map((folder) => (
                        <React.Fragment key={folder.id}>
                          <Box
                            onClick={() => onSelectFolder(folder.id)}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              setDragOverFolderId(folder.id);
                            }}
                            onDragLeave={() => setDragOverFolderId(null)}
                            onDrop={(e) => {
                              e.preventDefault();
                              setDragOverFolderId(null);
                              const convId = parseInt(e.dataTransfer.getData('text/plain'), 10);
                              if (convId && onMoveToFolder) onMoveToFolder(convId, folder.id);
                              setDragConvId(null);
                            }}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.5, cursor: 'pointer',
                              bgcolor: dragOverFolderId === folder.id ? '#dbeafe' : selectedFolderId === folder.id ? '#d5e3fd' : 'transparent',
                              '&:hover': { bgcolor: selectedFolderId === folder.id ? '#d5e3fd' : '#f1f4f5' },
                              border: dragOverFolderId === folder.id ? '1px dashed #526075' : '1px solid transparent',
                              borderRadius: 1,
                              transition: 'background-color 0.15s, border-color 0.15s',
                              '& .folder-actions': { opacity: 0 },
                              '&:hover .folder-actions': { opacity: 1 },
                            }}
                          >
                            {(() => {
                              const client = (folder.client_airtable_id || folder.client_name) && clients?.find((c) =>
                                (folder.client_airtable_id && c.id === folder.client_airtable_id) ||
                                c.name === folder.client_name ||
                                (folder.client_name && c.name && (
                                  folder.client_name.toLowerCase().includes(c.name.toLowerCase()) ||
                                  c.name.toLowerCase().includes(folder.client_name.toLowerCase())
                                ))
                              );
                              return client?.logo ? (
                                <Box
                                  component="img"
                                  src={client.logo}
                                  alt={folder.client_name}
                                  sx={{ width: 18, height: 18, borderRadius: 0.5, objectFit: 'contain', flexShrink: 0, bgcolor: '#f8f9fa' }}
                                />
                              ) : (
                                <FolderOpenIcon sx={{ fontSize: 14, color: folder.color || '#526075' }} />
                              );
                            })()}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography fontSize={12} fontWeight={selectedFolderId === folder.id ? 600 : 400} noWrap>
                                {folder.name}
                              </Typography>
                              {folder.client_name && (
                                <Typography fontSize={10} color="text.secondary" noWrap>{folder.client_name}</Typography>
                              )}
                            </Box>
                            <Typography fontSize={10} color="text.secondary">{folder.chat_count || 0}</Typography>
                            <IconButton
                              className="folder-actions"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setFolderMenuAnchor(e.currentTarget); setFolderMenuFolder(folder); }}
                              sx={{ p: 0.25, transition: 'opacity 0.15s' }}
                            >
                              <MoreVertIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                          <Collapse in={selectedFolderId === folder.id} timeout="auto">
                            <Box sx={{ pl: 2 }}>
                              {displayedConversations
                                .filter((c) => c.folder_id === folder.id)
                                .map(renderConvItem)}
                            </Box>
                          </Collapse>
                        </React.Fragment>
                      ))}
                    </Collapse>
                  </Box>
                )}

                {/* Group chats section */}
                {groupConvs.length > 0 && !selectedFolderId && (
                  <Box sx={{ mb: 0.5 }}>
                    <Box
                      onClick={() => setGroupChatsOpen(!groupChatsOpen)}
                      sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#f8f9fa' } }}
                    >
                      <Typography fontSize={11} fontWeight={700} color="text.secondary"
                        sx={{ flex: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Groepschats
                      </Typography>
                      {groupChatsOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: '#adb3b5' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#adb3b5' }} />}
                    </Box>
                    <Collapse in={groupChatsOpen}>
                      {groupConvs.map(renderConvItem)}
                    </Collapse>
                  </Box>
                )}

                {/* My chats section header */}
                {(groupConvs.length > 0 || (folders && folders.length > 0)) && !selectedFolderId && (
                  <Box
                    onClick={() => setMyChatsOpen(!myChatsOpen)}
                    sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#f8f9fa' } }}
                  >
                    <Typography fontSize={11} fontWeight={700} color="text.secondary"
                      sx={{ flex: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Je chats
                    </Typography>
                    {myChatsOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: '#adb3b5' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#adb3b5' }} />}
                  </Box>
                )}
              </>
            )}

            {/* Archive toggle */}
            <Box sx={{ px: 2, pb: 0.5 }}>
              <Button
                fullWidth size="small"
                startIcon={showArchive ? <ChatIcon /> : <ArchiveIcon />}
                onClick={onToggleArchive}
                sx={{
                  textTransform: 'none', fontSize: 11, color: showArchive ? '#526075' : '#adb3b5',
                  bgcolor: showArchive ? '#d5e3fd' : 'transparent',
                  '&:hover': { bgcolor: showArchive ? '#c8d9f7' : '#f1f4f5' },
                  borderRadius: 1.5, justifyContent: 'flex-start', px: 1.5,
                }}
              >
                {showArchive ? 'Terug naar gesprekken' : `Archief${archivedConversations.length > 0 ? ` (${archivedConversations.length})` : ''}`}
              </Button>
            </Box>

            {/* Conversation list */}
            <Collapse in={showArchive || (myChatsOpen && !selectedFolderId)}>
              <Box sx={{ px: 0.5 }}>
                {(showArchive ? displayedConversations : myConvs).map(renderConvItem)}
                {(showArchive ? displayedConversations : myConvs).length === 0 && (
                  <Typography sx={{ py: 3, textAlign: 'center', color: '#adb3b5', fontSize: 13 }}>
                    {showArchive ? 'Geen gearchiveerde gesprekken' : search ? 'Geen resultaten' : 'Geen gesprekken'}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        )}
        {tab === 1 && <AgentManager agents={agents} onRefresh={onRefreshAgents} userEmail={userEmail} />}
        {tab === 2 && <PromptManager prompts={prompts} onRefresh={onRefreshPrompts} userEmail={userEmail} agents={agents} />}
      </Box>

      {/* Three-dot context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 200, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}
      >
        <MenuItem onClick={() => handleMenuAction('pin')} sx={{ fontSize: 13 }}>
          <ListItemIcon>{menuConv?.is_pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}</ListItemIcon>
          <ListItemText>{menuConv?.is_pinned ? 'Losmaken' : 'Vastzetten'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('rename')} sx={{ fontSize: 13 }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Naam wijzigen</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('group')} sx={{ fontSize: 13 }}>
          <ListItemIcon><GroupAddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Groepschat beginnen</ListItemText>
        </MenuItem>
        {folders && folders.length > 0 && (
          [
            <MenuItem key="move-header" disabled sx={{ fontSize: 11, fontWeight: 700, minHeight: 28, opacity: '0.7 !important' }}>
              <ListItemIcon><FolderIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Verplaats naar project</ListItemText>
            </MenuItem>,
            ...folders.map((f) => (
              <MenuItem key={`move-${f.id}`} onClick={() => handleMenuAction('moveToFolder', f.id)}
                sx={{ fontSize: 13, pl: 4 }}
                disabled={menuConv?.folder_id === f.id}
              >
                <ListItemIcon><FolderOpenIcon sx={{ fontSize: 16, color: f.color || '#526075' }} /></ListItemIcon>
                <ListItemText>{f.name}</ListItemText>
              </MenuItem>
            )),
          ]
        )}
        {menuConv?.folder_id && (
          <MenuItem onClick={() => handleMenuAction('detach')} sx={{ fontSize: 13 }}>
            <ListItemIcon><LinkOffIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Chat losmaken</ListItemText>
          </MenuItem>
        )}
        {showArchive ? (
          <MenuItem onClick={() => handleMenuAction('unarchive')} sx={{ fontSize: 13 }}>
            <ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Terugzetten</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleMenuAction('archive')} sx={{ fontSize: 13 }}>
            <ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Archiveren</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleMenuAction('delete')} sx={{ fontSize: 13, color: '#ef4444' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
          <ListItemText>Verwijderen</ListItemText>
        </MenuItem>
      </Menu>

      {/* Folder context menu */}
      <Menu
        anchorEl={folderMenuAnchor}
        open={Boolean(folderMenuAnchor)}
        onClose={() => { setFolderMenuAnchor(null); setFolderMenuFolder(null); }}
        PaperProps={{ sx: { minWidth: 180, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}
      >
        <MenuItem onClick={() => { const f = folderMenuFolder; setFolderMenuAnchor(null); setFolderMenuFolder(null); if (f) onNewChatInFolder(f.id); }} sx={{ fontSize: 13 }}>
          <ListItemIcon><AddCommentIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Nieuw gesprek</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { const f = folderMenuFolder; setFolderMenuAnchor(null); setFolderMenuFolder(null); if (f) onEditFolder(f); }} sx={{ fontSize: 13 }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Instellingen</ListItemText>
        </MenuItem>
        {folderMenuFolder?.project_id ? (
          <MenuItem onClick={() => { const f = folderMenuFolder; setFolderMenuAnchor(null); setFolderMenuFolder(null); if (f) onUnpinProject(f.project_id); }} sx={{ fontSize: 13, color: '#ef4444' }}>
            <ListItemIcon><LinkOffIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
            <ListItemText>Project loskoppelen</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { const f = folderMenuFolder; setFolderMenuAnchor(null); setFolderMenuFolder(null); if (f) onDeleteFolder(f.id); }} sx={{ fontSize: 13, color: '#ef4444' }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
            <ListItemText>Verwijderen</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>Naam wijzigen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(); }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Annuleren</Button>
          <Button variant="contained" onClick={handleRenameSubmit}
            sx={{ bgcolor: '#526075', '&:hover': { bgcolor: '#465469' } }}>
            Opslaan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function ChatSidebar({
  conversations, archivedConversations = [], showArchive, onToggleArchive,
  selectedId, onSelect, onNewChat, onNewChatInFolder, onDelete, onArchive, onUnarchive,
  agents, prompts, onRefreshAgents, onRefreshPrompts, userEmail,
  open, onClose, isMobile,
  folders = [], selectedFolderId, onSelectFolder, onEditFolder, onDeleteFolder,
  onSearch, onPin, onRename, onDetachFromFolder, onStartGroupChat, onMoveToFolder,
  onOpenProjectBrowser, onUnpinProject, clients = [],
}) {
  const sidebarProps = {
    conversations, archivedConversations, showArchive, onToggleArchive,
    selectedId, onSelect, onNewChat, onNewChatInFolder, onDelete, onArchive, onUnarchive,
    agents, prompts, onRefreshAgents, onRefreshPrompts, userEmail,
    folders, selectedFolderId, onSelectFolder, onEditFolder, onDeleteFolder,
    onSearch, onPin, onRename, onDetachFromFolder, onStartGroupChat, onMoveToFolder,
    onOpenProjectBrowser, onUnpinProject, clients,
  };

  if (isMobile) {
    return (
      <Drawer anchor="left" open={open} onClose={onClose}
        sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, top: 64, height: 'calc(100% - 64px)' } }}
      >
        <SidebarContent {...sidebarProps} />
      </Drawer>
    );
  }

  if (!open) return null;
  return (
    <Box sx={{ borderRight: '1px solid rgba(45,51,53,0.08)', height: '100%' }}>
      <SidebarContent {...sidebarProps} />
    </Box>
  );
}
